/**
 * BHPH Account Detail Page
 * Complete account overview with payment history, collection actions, promises, and late fees.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getAccountPromises,
  getBHPHAccount,
  getCollectionHistory,
  getDelinquencyBucketColor,
  getDelinquencyBucketLabel,
  getLateFeeHistory,
  getPaymentHistory,
  logCollectionAction,
  waiveLateFee,
  type BHPHAccount,
  type CollectionAction,
  type LateFeeTransaction,
  type Payment,
} from '../../services/bhphApi';
import { staffPortalRequestsApi } from '../../services/staffPortalRequestsApi';

// ===== Main Component =====
const BHPHAccountDetailPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<
    'overview' | 'payments' | 'actions' | 'promises' | 'fees'
  >('overview');
  const [showQuickAction, setShowQuickAction] = useState(false);

  // Fetch account data
  const { data: account, isLoading: loadingAccount } = useQuery({
    queryKey: ['bhphAccount', accountId],
    queryFn: () => getBHPHAccount(accountId!),
    enabled: !!accountId,
  });

  // Fetch payment history
  const { data: payments, isLoading: loadingPayments } = useQuery({
    queryKey: ['paymentHistory', accountId],
    queryFn: () => getPaymentHistory(accountId!, 100),
    enabled: !!accountId,
  });

  // Fetch collection history
  const { data: collectionActions, isLoading: loadingActions } = useQuery({
    queryKey: ['collectionHistory', accountId],
    queryFn: () => getCollectionHistory(accountId!, 50),
    enabled: !!accountId,
  });

  // Fetch promise history
  const { data: promises, isLoading: loadingPromises } = useQuery({
    queryKey: ['accountPromises', accountId],
    queryFn: () => getAccountPromises(accountId!),
    enabled: !!accountId,
  });

  // Fetch late fee history
  const { data: lateFees, isLoading: loadingFees } = useQuery({
    queryKey: ['lateFeeHistory', accountId],
    queryFn: () => getLateFeeHistory(accountId!, 50),
    enabled: !!accountId,
  });

  // v2 servicing Slice 11 (D6): portal-request count for the quick-link
  // button below, keyed on the account's customer (not the account id --
  // requests are scoped to the borrower, not a specific account).
  const { data: portalRequests } = useQuery({
    queryKey: ['staffPortalRequests', 'byCustomer', account?.customer_id],
    queryFn: () => staffPortalRequestsApi.listForCustomer(account!.customer_id!),
    enabled: !!account?.customer_id,
  });

  if (loadingAccount) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading account details...</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <p className="text-red-600 text-lg">Account not found</p>
          <button
            onClick={() => navigate('/bhph/dashboard')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const delinquencyColor = getDelinquencyBucketColor(
    account.days_past_due <= 0
      ? 'current'
      : account.days_past_due <= 7
        ? 'early'
        : account.days_past_due <= 15
          ? 'moderate'
          : account.days_past_due <= 30
            ? 'serious'
            : account.days_past_due <= 60
              ? 'critical'
              : 'severe'
  );

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/bhph/worklist')}
          className="text-blue-600 hover:underline mb-2 flex items-center"
        >
          ← Back to Worklist
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{account.buyer_name}</h1>
            <p className="text-gray-600 mt-1">Deal: {account.deal_number || 'N/A'}</p>
          </div>
          <div className="flex gap-2">
            {account.customer_id && (
              <>
                <button
                  onClick={() => navigate(`/customers/${account.customer_id}`)}
                  className="bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
                >
                  👤 Customer Profile
                </button>
                <button
                  onClick={() => navigate(`/customers/${account.customer_id}/workspace`)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
                >
                  🏠 Open Workspace
                </button>
                <button
                  onClick={() =>
                    navigate(`/bhph/portal-requests?customer_id=${account.customer_id}`)
                  }
                  className="relative bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
                >
                  💬 Portal Requests
                  {portalRequests && portalRequests.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-white text-teal-700 text-xs font-bold">
                      {portalRequests.length}
                    </span>
                  )}
                </button>
              </>
            )}
            <button
              onClick={() => navigate(`/bhph/payment?account=${accountId}`)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
            >
              💵 Process Payment
            </button>
            <button
              onClick={() => setShowQuickAction(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150"
            >
              📝 Log Action
            </button>
          </div>
        </div>
      </div>

      {/* Account Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatusCard
          title="Account Status"
          value={account.account_status}
          color={account.account_status === 'ACTIVE' ? 'green' : 'gray'}
        />
        <StatusCard
          title="Total Balance"
          value={formatCurrency(account.total_balance_amount)}
          color={account.days_past_due > 0 ? 'red' : 'blue'}
        />
        <StatusCard
          title="Days Past Due"
          value={account.days_past_due.toString()}
          subtitle={getDelinquencyBucketLabel(
            account.days_past_due <= 0
              ? 'current'
              : account.days_past_due <= 7
                ? 'early'
                : account.days_past_due <= 15
                  ? 'moderate'
                  : account.days_past_due <= 30
                    ? 'serious'
                    : account.days_past_due <= 60
                      ? 'critical'
                      : 'severe'
          )}
          color={delinquencyColor as any}
        />
        <StatusCard
          title="Payment Amount"
          value={formatCurrency(account.payment_amount)}
          subtitle={`Due: ${formatDate(account.payment_due_date)}`}
          color="purple"
        />
      </div>

      {/* Vehicle & Account Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Vehicle Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Vehicle Information</h2>
          <div className="space-y-3">
            <InfoRow label="Vehicle" value={account.vehicle_year_make_model} />
            <InfoRow label="Deal Number" value={account.deal_number} />
            <InfoRow label="Payment Frequency" value={account.payment_frequency} />
            <InfoRow label="Payment Count" value={`${account.payment_count} payments made`} />
            <InfoRow label="Last Payment" value={formatDate(account.last_payment_date)} />
            <InfoRow label="Activation Date" value={formatDate(account.activation_date)} />
          </div>
        </div>

        {/* Balance Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Balance Breakdown</h2>
          <div className="space-y-3">
            <BalanceRow
              label="Principal"
              amount={account.balance_principal_amount}
              color="text-blue-600"
            />
            <BalanceRow
              label="Interest"
              amount={account.balance_interest_amount}
              color="text-orange-600"
            />
            <BalanceRow
              label="Late Fees"
              amount={account.balance_late_amount}
              color="text-red-600"
            />
            <BalanceRow
              label="Other Fees"
              amount={account.balance_other_amount}
              color="text-purple-600"
            />
            <BalanceRow
              label="Down Payment Balance"
              amount={account.balance_down_amount}
              color="text-gray-600"
            />
            <div className="pt-3 border-t-2 border-gray-300">
              <BalanceRow
                label="Total Balance"
                amount={account.total_balance_amount}
                color="text-gray-900 font-bold text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        {/* Tab Headers */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <TabButton
              label="Overview"
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            />
            <TabButton
              label={`Payments (${payments?.length || 0})`}
              active={activeTab === 'payments'}
              onClick={() => setActiveTab('payments')}
            />
            <TabButton
              label={`Actions (${collectionActions?.length || 0})`}
              active={activeTab === 'actions'}
              onClick={() => setActiveTab('actions')}
            />
            <TabButton
              label={`Promises (${promises?.length || 0})`}
              active={activeTab === 'promises'}
              onClick={() => setActiveTab('promises')}
            />
            <TabButton
              label={`Late Fees (${lateFees?.length || 0})`}
              active={activeTab === 'fees'}
              onClick={() => setActiveTab('fees')}
            />
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab account={account} />}
          {activeTab === 'payments' && (
            <PaymentsTab payments={payments} loading={loadingPayments} />
          )}
          {activeTab === 'actions' && (
            <ActionsTab actions={collectionActions} loading={loadingActions} />
          )}
          {activeTab === 'promises' && (
            <PromisesTab promises={promises} loading={loadingPromises} />
          )}
          {activeTab === 'fees' && (
            <LateFeesTab fees={lateFees} loading={loadingFees} accountId={accountId!} />
          )}
        </div>
      </div>

      {/* Quick Action Modal */}
      {showQuickAction && (
        <QuickActionModal
          accountId={accountId!}
          onClose={() => setShowQuickAction(false)}
          onSuccess={() => {
            setShowQuickAction(false);
            queryClient.invalidateQueries({ queryKey: ['collectionHistory', accountId] });
            queryClient.invalidateQueries({ queryKey: ['accountPromises', accountId] });
          }}
        />
      )}
    </div>
  );
};

// ===== Component: Status Card =====
interface StatusCardProps {
  title: string;
  value: string;
  subtitle?: string;
  color: 'blue' | 'red' | 'green' | 'gray' | 'purple' | 'yellow' | 'orange';
}

const StatusCard: React.FC<StatusCardProps> = ({ title, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-lg p-4`}>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold">{value}</p>
      {subtitle && <p className="text-xs mt-1">{subtitle}</p>}
    </div>
  );
};

// ===== Component: Info Row =====
interface InfoRowProps {
  label: string;
  value: string | null;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}:</span>
      <span className="font-medium text-gray-900">{value || 'N/A'}</span>
    </div>
  );
};

// ===== Component: Balance Row =====
interface BalanceRowProps {
  label: string;
  amount: number;
  color?: string;
}

const BalanceRow: React.FC<BalanceRowProps> = ({ label, amount, color = 'text-gray-900' }) => {
  return (
    <div className="flex justify-between">
      <span className="text-gray-600">{label}:</span>
      <span className={color}>{formatCurrency(amount)}</span>
    </div>
  );
};

// ===== Component: Tab Button =====
interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({ label, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 font-medium text-sm border-b-2 transition duration-150 ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );
};

// ===== Tab: Overview =====
interface OverviewTabProps {
  account: BHPHAccount;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ account }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Balance</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(account.total_balance_amount)}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Regular Payment</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(account.payment_amount)}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Payment Frequency</p>
            <p className="text-xl font-semibold text-gray-900">{account.payment_frequency}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Payments Made</p>
            <p className="text-xl font-semibold text-gray-900">{account.payment_count}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Dates</h3>
        <div className="space-y-2">
          <InfoRow label="Next Payment Due" value={formatDate(account.payment_due_date)} />
          <InfoRow label="Last Payment Date" value={formatDate(account.last_payment_date)} />
          <InfoRow label="Account Activated" value={formatDate(account.activation_date)} />
          <InfoRow label="Created" value={formatDate(account.created_at)} />
          <InfoRow label="Last Updated" value={formatDate(account.updated_at)} />
        </div>
      </div>
    </div>
  );
};

// ===== Tab: Payments =====
interface PaymentsTabProps {
  payments?: Payment[];
  loading: boolean;
}

const PaymentsTab: React.FC<PaymentsTabProps> = ({ payments, loading }) => {
  if (loading) {
    return <div className="text-center py-8">Loading payment history...</div>;
  }

  if (!payments || payments.length === 0) {
    return <div className="text-center py-8 text-gray-600">No payment history available</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Date
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Amount
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Method
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Allocation
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Processed By
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {payments.map((payment) => (
            <tr key={payment.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-900">{formatDate(payment.paid_date)}</td>
              <td className="px-4 py-3 text-sm font-semibold text-green-600">
                {formatCurrency(payment.paid_amount)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{payment.payment_method}</td>
              <td className="px-4 py-3 text-xs">
                <div className="space-y-1">
                  {payment.late_fees_applied_amt > 0 && (
                    <div>Late: {formatCurrency(payment.late_fees_applied_amt)}</div>
                  )}
                  {payment.interest_applied_amt > 0 && (
                    <div>Int: {formatCurrency(payment.interest_applied_amt)}</div>
                  )}
                  {payment.principal_applied_amt > 0 && (
                    <div>Prin: {formatCurrency(payment.principal_applied_amt)}</div>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{payment.created_by_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ===== Tab: Collection Actions =====
interface ActionsTabProps {
  actions?: CollectionAction[];
  loading: boolean;
}

const ActionsTab: React.FC<ActionsTabProps> = ({ actions, loading }) => {
  if (loading) {
    return <div className="text-center py-8">Loading collection history...</div>;
  }

  if (!actions || actions.length === 0) {
    return <div className="text-center py-8 text-gray-600">No collection actions recorded</div>;
  }

  return (
    <div className="space-y-4">
      {actions.map((action) => (
        <div key={action.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {action.action_type === 'CALL'
                  ? '📞'
                  : action.action_type === 'SMS'
                    ? '💬'
                    : action.action_type === 'PROMISE'
                      ? '🤝'
                      : action.action_type === 'EMAIL'
                        ? '📧'
                        : '📝'}
              </span>
              <div>
                <h4 className="font-semibold text-gray-900">{action.action_type}</h4>
                <p className="text-sm text-gray-600">{formatDateTime(action.action_date)}</p>
              </div>
            </div>
            <span className="text-sm text-gray-600">{action.created_by_name}</span>
          </div>

          {action.contact_method && (
            <div className="text-sm mb-2">
              <span className="text-gray-600">Method:</span>
              <span className="ml-2 font-medium">{action.contact_method}</span>
            </div>
          )}

          {action.contact_outcome && (
            <div className="text-sm mb-2">
              <span className="text-gray-600">Outcome:</span>
              <span className="ml-2 font-medium">{action.contact_outcome}</span>
            </div>
          )}

          {action.notes && (
            <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 mb-2">{action.notes}</div>
          )}

          {action.promise_amount && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm">
              <strong>Promise:</strong> {formatCurrency(action.promise_amount || 0)} by{' '}
              {formatDate(action.promise_date)}
              {action.promise_kept !== null && (
                <span
                  className={`ml-2 font-semibold ${action.promise_kept ? 'text-green-600' : 'text-red-600'}`}
                >
                  ({action.promise_kept ? '✓ Kept' : '✗ Broken'})
                </span>
              )}
            </div>
          )}

          {action.follow_up_required && (
            <div className="text-sm text-orange-600 mt-2">
              ⚠️ Follow-up required: {formatDate(action.follow_up_date)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ===== Tab: Promises =====
interface PromisesTabProps {
  promises?: CollectionAction[];
  loading: boolean;
}

const PromisesTab: React.FC<PromisesTabProps> = ({ promises, loading }) => {
  if (loading) {
    return <div className="text-center py-8">Loading promise history...</div>;
  }

  const promiseActions = promises?.filter((action) => action.promise_amount) || [];

  if (promiseActions.length === 0) {
    return <div className="text-center py-8 text-gray-600">No promises recorded</div>;
  }

  const pending = promiseActions.filter((p) => p.promise_kept === null);
  const kept = promiseActions.filter((p) => p.promise_kept === true);
  const broken = promiseActions.filter((p) => p.promise_kept === false);

  return (
    <div className="space-y-6">
      {/* Pending Promises */}
      {pending.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-yellow-700 mb-3">
            Pending Promises ({pending.length})
          </h3>
          <div className="space-y-3">
            {pending.map((promise) => (
              <PromiseCard key={promise.id} promise={promise} status="pending" />
            ))}
          </div>
        </div>
      )}

      {/* Kept Promises */}
      {kept.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-green-700 mb-3">
            Kept Promises ({kept.length})
          </h3>
          <div className="space-y-3">
            {kept.map((promise) => (
              <PromiseCard key={promise.id} promise={promise} status="kept" />
            ))}
          </div>
        </div>
      )}

      {/* Broken Promises */}
      {broken.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-red-700 mb-3">
            Broken Promises ({broken.length})
          </h3>
          <div className="space-y-3">
            {broken.map((promise) => (
              <PromiseCard key={promise.id} promise={promise} status="broken" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Component: Promise Card =====
interface PromiseCardProps {
  promise: CollectionAction;
  status: 'pending' | 'kept' | 'broken';
}

const PromiseCard: React.FC<PromiseCardProps> = ({ promise, status }) => {
  const bgColor =
    status === 'pending' ? 'bg-yellow-50' : status === 'kept' ? 'bg-green-50' : 'bg-red-50';
  const borderColor =
    status === 'pending'
      ? 'border-yellow-200'
      : status === 'kept'
        ? 'border-green-200'
        : 'border-red-200';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="font-semibold text-gray-900">
            {formatCurrency(promise.promise_amount || 0)}
          </p>
          <p className="text-sm text-gray-600">Due: {formatDate(promise.promise_date)}</p>
        </div>
        <span className="text-xs text-gray-500">{formatDate(promise.action_date)}</span>
      </div>
      {promise.notes && <p className="text-sm text-gray-700">{promise.notes}</p>}
    </div>
  );
};

// ===== Tab: Late Fees =====
interface LateFeesTabProps {
  fees?: LateFeeTransaction[];
  loading: boolean;
  accountId: string;
}

const LateFeesTab: React.FC<LateFeesTabProps> = ({ fees, loading, accountId }) => {
  const queryClient = useQueryClient();
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState<string | null>(null);

  const waiveFeeMutation = useMutation({
    mutationFn: ({ transactionId, reason }: { transactionId: string; reason: string }) =>
      waiveLateFee(transactionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lateFeeHistory', accountId] });
      queryClient.invalidateQueries({ queryKey: ['bhphAccount', accountId] });
      setShowWaiverModal(false);
      setSelectedFee(null);
      toast.success('Late fee waived successfully');
    },
    onError: (error: any) => {
      toast.error(`Error waiving fee: ${error.response?.data?.detail || error.message}`);
    },
  });

  if (loading) {
    return <div className="text-center py-8">Loading late fee history...</div>;
  }

  if (!fees || fees.length === 0) {
    return <div className="text-center py-8 text-gray-600">No late fees recorded</div>;
  }

  return (
    <div className="space-y-4">
      {fees.map((fee) => (
        <div key={fee.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-lg font-semibold text-red-600">{formatCurrency(fee.fee_amount)}</p>
              <p className="text-sm text-gray-600">Applied: {formatDate(fee.applied_date)}</p>
              <p className="text-xs text-gray-500">Days Past Due: {fee.days_past_due}</p>
            </div>
            <div className="text-right">
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                  fee.waived ? 'bg-gray-200 text-gray-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {fee.waived ? 'WAIVED' : 'ACTIVE'}
              </span>
              <p className="text-xs text-gray-500 mt-1">{fee.transaction_type}</p>
            </div>
          </div>

          {fee.waived ? (
            <div className="bg-gray-50 rounded p-2 text-sm">
              <p className="font-semibold">Waived by: {fee.waived_by_name}</p>
              <p className="text-gray-600">Date: {formatDate(fee.waived_date)}</p>
              <p className="text-gray-600">Reason: {fee.waived_reason}</p>
            </div>
          ) : (
            <button
              onClick={() => {
                setSelectedFee(fee.id);
                setShowWaiverModal(true);
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Waive this fee
            </button>
          )}

          {fee.notes && <p className="text-sm text-gray-600 mt-2">{fee.notes}</p>}
        </div>
      ))}

      {/* Waiver Modal */}
      {showWaiverModal && selectedFee && (
        <WaiverModal
          feeId={selectedFee}
          onSubmit={(reason) => waiveFeeMutation.mutate({ transactionId: selectedFee, reason })}
          onClose={() => {
            setShowWaiverModal(false);
            setSelectedFee(null);
          }}
          loading={waiveFeeMutation.isPending}
        />
      )}
    </div>
  );
};

// ===== Component: Waiver Modal =====
interface WaiverModalProps {
  feeId: string;
  onSubmit: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}

const WaiverModal: React.FC<WaiverModalProps> = ({ onSubmit, onClose, loading }) => {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason for waiving the fee');
      return;
    }
    onSubmit(reason);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Waive Late Fee</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Waiver *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Explain why this fee is being waived..."
              required
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Waive Fee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Component: Quick Action Modal =====
interface QuickActionModalProps {
  accountId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const QuickActionModal: React.FC<QuickActionModalProps> = ({ accountId, onClose, onSuccess }) => {
  const [actionType, setActionType] = useState('CALL');
  const [contactMethod, setContactMethod] = useState('PHONE');
  const [contactOutcome, setContactOutcome] = useState('CONTACTED');
  const [notes, setNotes] = useState('');
  const [isPromise, setIsPromise] = useState(false);
  const [promiseAmount, setPromiseAmount] = useState('');
  const [promiseDate, setPromiseDate] = useState('');

  const logActionMutation = useMutation({
    mutationFn: (data: {
      action_type: string;
      contact_method?: string;
      contact_outcome?: string;
      notes?: string;
      promise_amount?: number;
      promise_date?: string;
    }) => logCollectionAction(accountId, data),
    onSuccess: () => {
      onSuccess();
      toast.success('Action logged successfully');
    },
    onError: (error: any) => {
      toast.error(`Error logging action: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    logActionMutation.mutate({
      action_type: actionType,
      contact_method: contactMethod,
      contact_outcome: contactOutcome,
      notes,
      promise_amount: isPromise ? parseFloat(promiseAmount) : undefined,
      promise_date: isPromise ? promiseDate : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Log Collection Action</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="CALL">Phone Call</option>
              <option value="SMS">SMS</option>
              <option value="EMAIL">Email</option>
              <option value="VISIT">In-Person Visit</option>
              <option value="PROMISE">Promise to Pay</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Method</label>
            <select
              value={contactMethod}
              onChange={(e) => setContactMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="PHONE">Phone</option>
              <option value="SMS">SMS</option>
              <option value="EMAIL">Email</option>
              <option value="IN_PERSON">In Person</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Outcome</label>
            <select
              value={contactOutcome}
              onChange={(e) => setContactOutcome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="CONTACTED">Contacted</option>
              <option value="NO_ANSWER">No Answer</option>
              <option value="LEFT_MESSAGE">Left Message</option>
              <option value="WRONG_NUMBER">Wrong Number</option>
              <option value="REFUSED">Refused to Pay</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Add details about the contact..."
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isPromise}
                onChange={(e) => setIsPromise(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Customer made a promise to pay
              </span>
            </label>
          </div>

          {isPromise && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Promise Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={promiseAmount}
                  onChange={(e) => setPromiseAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0.00"
                  required={isPromise}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Promise Date</label>
                <input
                  type="date"
                  value={promiseDate}
                  onChange={(e) => setPromiseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required={isPromise}
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={logActionMutation.isPending}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
            >
              {logActionMutation.isPending ? 'Logging...' : 'Log Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BHPHAccountDetailPage;
